import { CommonModule } from '@angular/common';
import type { Type } from '@angular/core';
import { Component, computed, HostBinding, input } from '@angular/core';

import type { InputsType } from 'ng-dynamic-component';
import { DynamicModule } from 'ng-dynamic-component';

import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import type { PersuadeCardLinkList } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.interface';
import type { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import { LinkListComponent as SbLinkListComponent } from '@valk-nx/storyblok/components/link-list/src/lib/link-list';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type {
	Button,
	ButtonList,
	CardImages,
	HeadingGroupSection,
	ISbStoryBlok,
	LinkList,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

const Components = {
	linkList: SbLinkListComponent,
	richText: RichTextComponent,
} as const satisfies Record<'linkList' | 'richText', Type<unknown>>;

const EMPTY_LINKLIST: PersuadeCardLinkList = Object.freeze({
	items: [],
	showIcons: false,
	showDivider: false,
	useChevron: false,
});

@Component({
	selector: 'sb-card',
	templateUrl: './card.html',
	imports: [
		CommonModule,
		DynamicModule,
		PersuadeCardComponent,
		StoryblokDirective,
	],
	host: { class: 'w-full' },
})
export class CardComponent extends StoryblokRootDirective {
	readonly images = input<CardImages[] | undefined>();
	readonly heading = input<HeadingGroupSection[] | undefined>();
	readonly content = input<ISbStoryBlok[] | undefined>();
	readonly footer = input<LinkList[] | ButtonList | Button[] | undefined>();

	isHorizontal = input<boolean>(false);
	stopGrow = input<boolean>(false);

	readonly components: typeof Components = Components;

	readonly flatImages = computed(
		() =>
			this.images()
				?.filter(
					({ bynder }) => !!bynder?.[0]?.files?.transformBaseUrl?.url,
				)
				.map(({ bynder, imageAlt }) =>
					StoryblokHelper.mapBynderToPhotoInterface(
						bynder[0],
						imageAlt,
					),
				) ?? [],
	);

	readonly headingInputs = computed<(InputsType & { component: string })[]>(
		() => {
			const src =
				this.heading() ??
				this.content()?.filter((v) => v?.component === 'headingGroup');
			return (
				src?.map((blok) => ({
					...blok,
					component: blok?.['component'] ?? '',
				})) ?? []
			);
		},
	);

	readonly contentInputs = computed<(InputsType & { component: string })[]>(
		() =>
			this.content()?.map((blok) => ({
				...blok,
				component: blok?.['component'] ?? '',
			})) ?? [],
	);

	readonly buttons = computed(() => {
		const footer = this.footer();

		const button = footer as Button[];
		const buttonList = footer as ButtonList;

		if (!footer) return null;

		if (buttonList[0]?.component === 'buttonList') {
			return {
				ctaText: buttonList[0].buttons?.[0]?.text,
				ctaUrl: buttonList[0].buttons?.[0]?.link?.cached_url,
				ctaLinkType: buttonList[0].buttons?.[0]?.link?.linktype,
				secondaryCtaText: buttonList[0].buttons?.[1]?.text,
				secondaryCtaUrl: buttonList[0].buttons?.[1]?.link?.cached_url,
				secondaryCtaLinkType:
					buttonList[0].buttons?.[1]?.link?.linktype,
			};
		} else {
			const primaryButton =
				button.find(
					(blok): blok is Button => blok?.component === 'button',
				) ?? null;
			return {
				ctaType: `btn-${primaryButton?.type}`,
				ctaText: primaryButton?.text,
				ctaUrl: primaryButton?.link?.cached_url,
				ctaLinkType: primaryButton?.link?.linktype,
				secondaryCtaText: undefined,
				secondaryCtaUrl: undefined,
			};
		}
	});

	readonly linkList = computed<PersuadeCardLinkList>(() => {
		const list = (this.footer() as LinkList[])?.find(
			(blok): blok is LinkList => blok?.component === 'linkList',
		);
		if (!list) {
			return EMPTY_LINKLIST;
		}

		const items = (list.blocks ?? []).map((item) => {
			const link = item.link ?? item.url;
			const rawUrl = link?.cached_url ?? link?.url ?? '';
			const targetSelf = link?.target !== '_blank';
			const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const isEmail =
				emailPattern.test(rawUrl) &&
				!rawUrl.startsWith('mailto:') &&
				!rawUrl.startsWith('http');
			const url = isEmail ? `mailto:${rawUrl}` : rawUrl;

			return {
				url,
				targetSelf,
				icon: item.icon,
				text: item.text,
			};
		});

		return {
			items,
			showIcons: !!list.showIcons,
			showDivider: !!list.showDivider,
			useChevron: !!list.useChevron,
		};
	});

	headingType = computed<HeadingType>(() => {
		const heading = this.headingInputs()[0];
		if (heading) {
			return heading['headingType'] as HeadingType;
		}
		return undefined;
	});

	headingTitle = computed<string>(() => {
		const heading = this.headingInputs()[0];
		if (heading) {
			return heading['title'] as string;
		}
		return undefined;
	});

	headingOverline = computed<string>(() => {
		const heading = this.headingInputs()[0];
		if (heading) {
			return heading['overline'] as string;
		}
		return undefined;
	});

	buttonCtaType = computed<'btn-primary' | 'btn-secondary'>(() => {
		const button = this.buttons();
		if (button?.ctaType) {
			return button.ctaType as 'btn-primary' | 'btn-secondary';
		}
		return 'btn-primary';
	});

	@HostBinding('class.h-full')
	get shouldHaveFullHeight(): boolean {
		return !this.stopGrow();
	}
}
